import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import ConcalLogo from '../ConcalLogo';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user, userProfile, signOut, isOfflineMode, disableOfflineMode } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleOfflineMode = () => {
    if (isOfflineMode) {
      disableOfflineMode();
      navigate('/login');
    }
  };

  // Navigation items
  const navigationItems = [
    { path: '/', label: 'Upload contracts', icon: 'Upload' },
    { path: '/contract-history', label: 'Contract history', icon: 'FileText' }
  ];

  // Check if current path is active
  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-header bg-card/80 backdrop-blur-md border-b border-border/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-3">
              <ConcalLogo size="large" className="w-auto h-10" />
            </Link>
            
            {/* Offline Mode Indicator */}
            {isOfflineMode && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-primary/10 rounded-full">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                <span className="text-xs text-primary font-medium">
                  Demo Mode
                </span>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">
                  {userProfile?.full_name || user?.email || 'Demo User'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isOfflineMode ? 'Demo Mode' : (userProfile?.role?.charAt(0)?.toUpperCase() + userProfile?.role?.slice(1) || 'Agent')}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {isOfflineMode && (
                  <button
                    onClick={toggleOfflineMode}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200"
                    title="Exit Demo Mode"
                  >
                    <Icon name="Database" size={18} />
                  </button>
                )}
                
                <button
                  onClick={handleSignOut}
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200"
                  title="Sign Out"
                >
                  <Icon name="LogOut" size={18} />
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200"
            >
              <Icon name={isMenuOpen ? 'X' : 'Menu'} size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border/50 py-4">
            <nav className="space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile User Info */}
            <div className="border-t border-border/50 mt-4 pt-4 px-4">
              <div className="mb-3">
                <div className="text-sm font-medium text-foreground">
                  {userProfile?.full_name || user?.email || 'Demo User'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isOfflineMode ? 'Demo Mode' : (userProfile?.role?.charAt(0)?.toUpperCase() + userProfile?.role?.slice(1) || 'Agent')}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {isOfflineMode && (
                  <button
                    onClick={toggleOfflineMode}
                    className="flex items-center space-x-2 px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200"
                  >
                    <Icon name="Database" size={18} />
                    <span>Exit Demo Mode</span>
                  </button>
                )}
                
                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-2 px-4 py-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors duration-200"
                >
                  <Icon name="LogOut" size={18} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
